import React from 'react';
import { Autocomplete, Box, Chip, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * What kind of work this bet is.
 *
 * Free typing, because a controlled vocabulary needs an admin to maintain it and
 * would be wrong the first time a team bets on something nobody anticipated. What
 * keeps it from fragmenting is the suggestion list: existing tags are offered as
 * you type, ordered by how much the team already uses them, so reaching for the
 * one that exists is easier than inventing a near-duplicate.
 *
 * Matching mirrors the server's slug rules, so "Billing" finds an existing
 * "billing" before a second one gets created. The server normalizes again on write
 * and is the authority; this only decides what to suggest.
 */

export const MAX_TAG_LENGTH = 32;
export const MAX_TAGS = 8;

export const tagSlug = (raw) => String(raw || '')
  .toLowerCase()
  .trim()
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/-{2,}/g, '-')
  .replace(/^-+|-+$/g, '');

const TagPicker = ({
  value = [],
  onChange,
  suggestions = [],
  derived = [],
  derivedLoading = false,
  disabled = false,
  label = 'Tags',
  placeholder = 'billing, infra, customer request…',
  helperText,
}) => {
  const chosenSlugs = new Set(value.map(tag => tagSlug(tag.label || tag)));
  const available = suggestions.filter(tag => !chosenSlugs.has(tag.slug));
  const atLimit = value.length >= MAX_TAGS;
  // Guesses about this specific bet, as opposed to `suggestions`, which is the
  // organization's whole vocabulary offered inside the input. These sit outside the
  // input as one-click chips because each carries a reason worth reading.
  const unaccepted = derived.filter(tag => !chosenSlugs.has(tag.slug));

  const accept = (tag) => {
    if (chosenSlugs.has(tag.slug) || value.length >= MAX_TAGS) return;
    onChange([...value, { slug: tag.slug, label: tag.label }]);
  };

  const acceptAll = () => {
    const next = [...value];
    const seen = new Set(chosenSlugs);
    for (const tag of unaccepted) {
      if (next.length >= MAX_TAGS || seen.has(tag.slug)) continue;
      seen.add(tag.slug);
      next.push({ slug: tag.slug, label: tag.label });
    }
    onChange(next);
  };

  // Autocomplete hands back a mix of strings (freely typed) and suggestion
  // objects (picked from the list), so both are flattened to a label here and
  // deduplicated on the slug the server will use.
  const commit = (raw) => {
    const seen = new Set();
    const next = [];
    for (const item of raw) {
      const text = String(typeof item === 'string' ? item : item.label || '')
        .trim()
        .slice(0, MAX_TAG_LENGTH);
      const slug = tagSlug(text);
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      next.push({ slug, label: text });
    }
    onChange(next.slice(0, MAX_TAGS));
  };

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        size="small"
        disabled={disabled}
        options={available}
        value={value}
        onChange={(event, next) => commit(next)}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
        // Compared on the slug rather than the label, so typing "Billing" matches
        // an existing "billing" instead of offering to create a duplicate.
        filterOptions={(options, state) => {
          const query = tagSlug(state.inputValue);
          if (!query) return options;
          return options.filter(option => option.slug.includes(query));
        }}
        renderTags={(tags, getTagProps) => tags.map((tag, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={typeof tag === 'string' ? tag : tag.label}
              size="small"
              {...rest}
            />
          );
        })}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box component="li" key={option.slug} {...rest}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.betCount} {option.betCount === 1 ? 'bet' : 'bets'}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={atLimit ? '' : placeholder}
            helperText={atLimit ? `That is the most tags a bet can carry (${MAX_TAGS}).` : helperText}
          />
        )}
      />

      {derivedLoading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Looking at what this bet touched…
        </Typography>
      )}

      {!derivedLoading && unaccepted.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Suggested from this bet
            </Typography>
            {unaccepted.length > 1 && !atLimit && (
              <Typography
                variant="caption"
                onClick={disabled ? undefined : acceptAll}
                sx={{
                  ml: 0.5,
                  cursor: disabled ? 'default' : 'pointer',
                  color: 'primary.main',
                  '&:hover': { textDecoration: disabled ? 'none' : 'underline' },
                }}
              >
                add all
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {unaccepted.map(tag => (
              <Tooltip key={tag.slug} title={tag.reason || 'Suggested for this bet'}>
                <Chip
                  label={tag.label}
                  size="small"
                  variant="outlined"
                  icon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={disabled || atLimit ? undefined : () => accept(tag)}
                  disabled={disabled || atLimit}
                  sx={{ borderStyle: 'dashed' }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TagPicker;
